import { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, X } from 'lucide-react';
import { apiService } from '../services/api.service';
import { formatMoney } from '../pages/treasury/treasuryUi';

type Props = {
  loan: any;
  onClose: () => void;
  onCompleted: () => void;
  onError: (message: string) => void;
};

function humanize(raw: unknown): string {
  const msg = Array.isArray(raw) ? raw.join(' ') : String((raw as any)?.message ?? raw ?? '');
  if (!msg || msg === 'undefined') return 'No se pudo completar la transferencia.';
  if (msg.length > 200) return 'No se pudo completar. Revisá cuenta, comprobante y permisos.';
  return msg;
}

function ocrVal(v: unknown): string {
  if (v == null || v === '') return '—';
  return String(v);
}

export function CompleteBankDisbursementModal({ loan, onClose, onCompleted, onError }: Props) {
  const [bankAccountId, setBankAccountId] = useState('');
  const [bankAccounts, setBankAccounts] = useState<any[]>([]);
  const [bankBalances, setBankBalances] = useState<Record<string, number>>({});
  const [file, setFile] = useState<File | null>(null);
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState('');
  const [result, setResult] = useState<any | null>(null);

  const creditAmount = Number(loan.amount) || 0;
  const ocr = result?.transferProofOcr ?? null;
  const mismatch = Boolean(result?.transferProofAmountMismatch || ocr?.amountMismatch);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const accounts = await apiService.listDisbursementBankAccounts({
          branchId: loan.branchId || undefined,
          legalEntityId: loan.legalEntityId || loan.branch?.legalEntityId || undefined,
        });
        if (cancelled) return;
        setBankAccounts(accounts ?? []);
        const le = loan.legalEntityId || loan.branch?.legalEntityId;
        if (le) {
          const summary = await apiService.getTreasurySummary(le).catch(() => null);
          if (cancelled) return;
          const map: Record<string, number> = {};
          for (const b of summary?.banks ?? []) map[b.id] = Number(b.balance ?? 0);
          setBankBalances(map);
        }
      } catch {
        if (!cancelled) setBankAccounts([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loan]);

  const selectedBank = bankAccounts.find((a) => a.id === bankAccountId);

  const confirm = async () => {
    if (busy) return;
    if (!bankAccountId) {
      setLocalError('Seleccioná la cuenta bancaria de salida.');
      return;
    }
    if (!file) {
      setLocalError('Adjuntá el comprobante de transferencia (PDF o imagen).');
      return;
    }
    const okType =
      file.type === 'application/pdf' ||
      file.name.toLowerCase().endsWith('.pdf') ||
      file.type.startsWith('image/') ||
      /\.(jpe?g|png|webp|gif)$/i.test(file.name);
    if (!okType) {
      setLocalError('El comprobante debe ser PDF o imagen (JPG/PNG/WEBP).');
      return;
    }

    setBusy(true);
    setLocalError('');
    try {
      const updated = await apiService.completeBankDisbursement(loan.id, {
        bankAccountId,
        reference: reference.trim() || undefined,
        file,
      });
      setResult(updated);
    } catch (e: any) {
      const msg = humanize(e?.response?.data?.message ?? e.message);
      setLocalError(msg);
      onError(msg);
    } finally {
      setBusy(false);
    }
  };

  if (result) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 overflow-y-auto">
        <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 my-6 space-y-4">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Transferencia registrada</h3>
              <p className="text-sm text-slate-500 mt-0.5">
                Crédito otorgado. Datos leídos del comprobante:
              </p>
            </div>
            <button
              type="button"
              onClick={() => onCompleted()}
              className="p-1 rounded hover:bg-slate-100"
              aria-label="Cerrar"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {mismatch ? (
            <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 space-y-1">
              <p className="font-semibold flex items-center gap-1.5">
                <AlertTriangle className="h-4 w-4" /> Monto distinto al del crédito
              </p>
              <p>
                Esperado: <strong>{formatMoney(creditAmount)}</strong>
              </p>
              <p className="text-red-700 font-semibold">
                En comprobante:{' '}
                {ocr?.amount != null ? formatMoney(Number(ocr.amount)) : '—'}
                {ocr?.amountDelta != null
                  ? ` (Δ ${ocr.amountDelta > 0 ? '+' : ''}${formatMoney(Number(ocr.amountDelta))})`
                  : ''}
              </p>
            </div>
          ) : (
            <p className="text-sm text-emerald-800 flex items-center gap-1.5">
              <CheckCircle2 className="h-4 w-4" /> Monto del comprobante coincide con el crédito.
            </p>
          )}

          {ocr?.ocrError && (
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              OCR con aviso: {ocr.ocrError}
            </p>
          )}

          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
            <div>
              <dt className="text-slate-500">Nº operación</dt>
              <dd className="font-medium">{ocrVal(ocr?.operationNumber)}</dd>
            </div>
            <div>
              <dt className="text-slate-500">Fecha / hora</dt>
              <dd className="font-medium">
                {ocrVal(ocr?.date)}
                {ocr?.time ? ` ${ocr.time}` : ''}
              </dd>
            </div>
            <div>
              <dt className="text-slate-500">Banco</dt>
              <dd className="font-medium">{ocrVal(ocr?.bank)}</dd>
            </div>
            <div>
              <dt className="text-slate-500">Concepto</dt>
              <dd className="font-medium">{ocrVal(ocr?.concept)}</dd>
            </div>
            <div className="sm:col-span-2 border-t pt-2 mt-1">
              <dt className="text-slate-500">Destino</dt>
              <dd className="font-medium">
                {ocrVal(ocr?.destinationHolder)} · {ocrVal(ocr?.destinationAlias)} ·{' '}
                {ocrVal(ocr?.destinationCbu)}
              </dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="text-slate-500">Origen</dt>
              <dd className="font-medium">
                {ocrVal(ocr?.originHolder)} · {ocrVal(ocr?.originAlias)} · {ocrVal(ocr?.originCbu)}
              </dd>
            </div>
          </dl>

          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => onCompleted()}
              className="px-4 py-2 bg-slate-900 text-white rounded-md hover:bg-slate-800"
            >
              Listo
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 my-6 space-y-4">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Completar transferencia</h3>
            <p className="text-sm text-slate-500 mt-0.5">
              {loan.customer?.firstName} {loan.customer?.lastName} — {formatMoney(creditAmount)}
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-slate-100" aria-label="Cerrar">
            <X className="h-4 w-4" />
          </button>
        </div>

        <p className="text-sm text-slate-700">
          Elegí la cuenta y subí el comprobante (PDF o imagen). Al confirmar, la IA lee los datos y
          registra el desembolso. Si el monto no coincide, se marca en rojo pero no se bloquea.
        </p>

        <label className="block text-sm">
          <span className="font-medium text-slate-800">Cuenta bancaria</span>
          <select
            value={bankAccountId}
            onChange={(e) => setBankAccountId(e.target.value)}
            className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
          >
            <option value="">Seleccionar banco…</option>
            {bankAccounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.bankName}
                {a.branch?.name ? ` · ${a.branch.name}` : ''}
                {a.accountNumber ? ` · ${a.accountNumber}` : a.alias ? ` · ${a.alias}` : ''}
                {bankBalances[a.id] != null ? ` · ${formatMoney(bankBalances[a.id])}` : ''}
              </option>
            ))}
          </select>
          {selectedBank && bankBalances[selectedBank.id] != null && (
            <p className="text-xs text-slate-600 mt-1">
              Disponible ledger: {formatMoney(bankBalances[selectedBank.id])}
            </p>
          )}
          {bankAccounts.length === 0 && (
            <span className="block text-xs text-amber-700 mt-1">
              No hay cuentas activas para esta empresa/sucursal.
            </span>
          )}
        </label>

        <label className="block text-sm">
          <span className="font-medium text-slate-800">Comprobante (PDF o imagen)</span>
          <input
            type="file"
            accept="application/pdf,.pdf,image/*,.jpg,.jpeg,.png,.webp"
            className="mt-1 block w-full text-sm"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
          {file && (
            <p className="text-xs text-emerald-800 mt-1 flex items-center gap-1">
              <CheckCircle2 className="h-3.5 w-3.5" /> {file.name}
            </p>
          )}
        </label>

        <label className="block text-sm">
          <span className="font-medium text-slate-800">Referencia (opcional)</span>
          <input
            type="text"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            placeholder="Si vacío, se usa el Nº del OCR"
          />
        </label>

        {localError && (
          <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
            {localError}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
            disabled={busy}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => void confirm()}
            disabled={busy || !bankAccountId || !file}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
          >
            {busy ? 'Leyendo comprobante y desembolsando…' : 'Confirmar desembolso'}
          </button>
        </div>
      </div>
    </div>
  );
}
